import React, { useEffect, useState } from 'react';
import { Button, Chip, Grid, List, ListItem, Paper, Tab, Tabs, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import DescriptionIcon from '@material-ui/icons/Description';
import LinkIcon from '@material-ui/icons/Link';
import API_Service, { axiosDefaultConfig } from '../../../Services/API_Service';
import { req_announcement_inbox, req_announcement_read } from '../../../Helper/Constants';
import ImageViewer from '../../../Components/ImageViewer';
import { getVideoLinkInfo } from '../announcementUtils';

const useStyles = makeStyles((theme) => ({
	tabs: {
		marginBottom: theme.spacing(1)
	},
	listPaper: {
		borderRadius: 10,
		maxHeight: '72vh',
		overflowY: 'auto'
	},
	inboxItem: {
		borderLeft: '3px solid transparent',
		alignItems: 'flex-start'
	},
	inboxItemSelected: {
		borderLeftColor: '#FEF200',
		backgroundColor: theme.palette.action.selected
	},
	dotUnread: {
		fontSize: 10,
		color: '#FEF200',
		marginTop: 6,
		marginRight: theme.spacing(1)
	},
	dotRead: {
		fontSize: 10,
		color: theme.palette.divider,
		marginTop: 6,
		marginRight: theme.spacing(1)
	},
	inboxItemBody: {
		minWidth: 0,
		flex: 1
	},
	unreadTitle: {
		fontWeight: 700
	},
	inboxItemMeta: {
		fontSize: 11,
		color: theme.palette.text.secondary
	},
	readingPane: {
		borderRadius: 10,
		padding: theme.spacing(3),
		maxHeight: '72vh',
		overflowY: 'auto'
	},
	metaRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2)
	},
	priorityChip: {
		fontWeight: 500,
		fontSize: 11,
		borderRadius: 6,
		height: 22
	},
	priorityInfo: {
		backgroundColor: 'rgba(184, 184, 184, 0.15)'
	},
	priorityImportant: {
		backgroundColor: 'rgba(254, 242, 0, 0.2)',
		color: '#c9b800'
	},
	priorityCritical: {
		backgroundColor: 'rgba(255, 94, 91, 0.13)',
		color: '#FF5E5B'
	},
	metaDate: {
		fontSize: 12,
		color: theme.palette.text.secondary
	},
	articleTitle: {
		fontSize: 24,
		fontWeight: 500,
		marginBottom: theme.spacing(2)
	},
	articleBody: {
		whiteSpace: 'pre-wrap',
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(3)
	},
	attachSection: {
		marginTop: theme.spacing(2)
	},
	thumbRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(1)
	},
	thumb: {
		width: 120,
		height: 90,
		objectFit: 'cover',
		borderRadius: 6,
		cursor: 'pointer'
	},
	videoPlayer: {
		width: '100%',
		maxHeight: 380,
		borderRadius: 6,
		marginTop: theme.spacing(1)
	},
	videoWrap: {
		position: 'relative',
		paddingTop: '56.25%',
		marginTop: theme.spacing(1)
	},
	videoFrame: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		border: 0,
		borderRadius: 6
	},
	fileBtn: {
		textTransform: 'none',
		marginRight: theme.spacing(1),
		marginTop: theme.spacing(1)
	},
	emptyText: {
		padding: theme.spacing(3),
		color: theme.palette.text.secondary
	},
	skeletonPane: {
		borderRadius: 10
	}
}));

export default function InboxTab({ language, client }) {
	const classes = useStyles();
	const { enqueueSnackbar } = useSnackbar();

	const [loading, setLoading] = useState(true);
	const [list, setList] = useState([]);
	const [tab, setTab] = useState(0);
	const [selectedId, setSelectedId] = useState(null);
	const [viewerImage, setViewerImage] = useState(null);

	const snack = (msg, type = 'success') => {
		enqueueSnackbar(msg, {
			variant: type,
			preventDuplicate: true,
			autoHideDuration: type === 'success' ? 2000 : 3000
		});
	};

	useEffect(() => {
		if (client) {
			loadInbox();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client]);

	const loadInbox = () => {
		const formdata = new FormData();
		API_Service.post(req_announcement_inbox, formdata, axiosDefaultConfig(client)).then(({ data }) => {
			if (parseInt(data.result) === 1) {
				setList(data.extraData || []);
			} else {
				setList([]);
				snack(data.message || 'Error', 'error');
			}
			setLoading(false);
		}).catch(() => {
			snack('Network error', 'error');
			setLoading(false);
		});
	};

	const markAsRead = (item) => {
		const formdata = new FormData();
		formdata.append('announcement_id', item.id);
		API_Service.post(req_announcement_read, formdata, axiosDefaultConfig(client)).then(({ data }) => {
			if (parseInt(data.result) === 1) {
				window.dispatchEvent(new Event('announcement-read'));
			} else {
				snack(data.message || 'Error', 'error');
			}
		}).catch(() => snack('Network error', 'error'));
	};

	const handleSelect = (item) => {
		setSelectedId(item.id);
		if (parseInt(item.read_status) !== 1) {
			markAsRead(item);
			setList(prev => prev.map(row => (row.id === item.id ? { ...row, read_status: 1 } : row)));
		}
	};

	const parseAttachments = (item) => {
		try {
			return JSON.parse(item.attachment || '[]') || [];
		} catch (e) {
			return [];
		}
	};

	const priorityLabel = (p) => {
		if (p === 'important') return language.announcement_priority_important;
		if (p === 'critical') return language.announcement_priority_critical;
		return language.announcement_priority_info;
	};

	const priorityClass = (p) => {
		if (p === 'important') return classes.priorityImportant;
		if (p === 'critical') return classes.priorityCritical;
		return classes.priorityInfo;
	};

	const visible = tab === 1 ? list.filter(i => parseInt(i.read_status) !== 1) : list;
	const selected = list.find(i => i.id === selectedId) || null;
	const unreadCount = list.filter(i => parseInt(i.read_status) !== 1).length;

	const renderAttachment = (item, index) => {
		if (item.type === 'youtube') {
			const info = getVideoLinkInfo(item.url);
			if (!info) return null;
			if (info.kind === 'youtube' || info.kind === 'drive') {
				return (
					<div key={String(index)} className={classes.videoWrap}>
						<iframe
							className={classes.videoFrame}
							src={info.embedUrl}
							title={`${info.kind}-${index}`}
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
							allowFullScreen
						/>
					</div>
				);
			}
			if (info.kind === 'mp4') {
				return (
					<video key={String(index)} controls className={classes.videoPlayer} src={info.embedUrl} />
				);
			}
			return (
				<Button
					key={String(index)}
					variant="outlined"
					size="small"
					className={classes.fileBtn}
					startIcon={<LinkIcon />}
					onClick={() => window.open(info.embedUrl, '_blank')}>
					{item.name || info.embedUrl}
				</Button>
			);
		}
		if (item.type === 'video') {
			return (
				<video key={String(index)} controls className={classes.videoPlayer} src={item.url} />
			);
		}
		if (item.type === 'image') {
			return (
				<img
					key={String(index)}
					src={item.url}
					alt={item.name}
					className={classes.thumb}
					onClick={() => setViewerImage(item.url)}
				/>
			);
		}
		return (
			<Button
				key={String(index)}
				variant="outlined"
				size="small"
				className={classes.fileBtn}
				startIcon={<DescriptionIcon />}
				onClick={() => window.open(item.url, '_blank')}>
				{item.name}
			</Button>
		);
	};

	if (loading) {
		return (
			<Grid container spacing={2}>
				<Grid item xs={12} sm={4}>
					<Skeleton animation="wave" variant="rect" height={400} className={classes.skeletonPane} />
				</Grid>
				<Grid item xs={12} sm={8}>
					<Skeleton animation="wave" variant="rect" height={400} className={classes.skeletonPane} />
				</Grid>
			</Grid>
		);
	}

	return (
		<div>
			<Tabs
				className={classes.tabs}
				indicatorColor="primary"
				textColor="inherit"
				value={tab}
				onChange={(e, v) => setTab(v)}>
				<Tab label={language.announcement_all} />
				<Tab label={`${language.announcement_unread}${unreadCount > 0 ? ` (${unreadCount})` : ''}`} />
			</Tabs>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={4}>
					<Paper variant="outlined" className={classes.listPaper}>
						{visible.length === 0 ? (
							<Typography className={classes.emptyText}>{language.announcement_no_data}</Typography>
						) : (
							<List disablePadding>
								{visible.map((item) => {
									const attachments = parseAttachments(item);
									return (
										<ListItem
											button
											key={String(item.id)}
											onClick={() => handleSelect(item)}
											className={`${classes.inboxItem} ${selectedId === item.id ? classes.inboxItemSelected : ''}`}>
											<FiberManualRecordIcon className={parseInt(item.read_status) === 1 ? classes.dotRead : classes.dotUnread} />
											<div className={classes.inboxItemBody}>
												<Typography variant="body2" noWrap className={parseInt(item.read_status) === 1 ? '' : classes.unreadTitle}>
													{item.title}
												</Typography>
												<Typography className={classes.inboxItemMeta}>
													{item.publish_date ? moment(item.publish_date, 'YYYY-MM-DD').format('DD/MM/YYYY') : ''}
													{attachments.length > 0 ? ` · ${attachments.length} ${language.announcement_attachments}` : ''}
												</Typography>
											</div>
										</ListItem>
									);
								})}
							</List>
						)}
					</Paper>
				</Grid>
				<Grid item xs={12} sm={8}>
					<Paper variant="outlined" className={classes.readingPane}>
						{!selected ? (
							<Typography className={classes.emptyText}>{language.announcement_select_to_read}</Typography>
						) : (
							<div>
								<div className={classes.metaRow}>
									<Chip
										size="small"
										label={priorityLabel(selected.priority)}
										className={`${classes.priorityChip} ${priorityClass(selected.priority)}`}
									/>
									<Typography className={classes.metaDate}>
										{selected.publish_date ? moment(selected.publish_date, 'YYYY-MM-DD').format('DD/MM/YYYY') : ''}
									</Typography>
								</div>
								<Typography className={classes.articleTitle}>{selected.title}</Typography>
								<Typography variant="body2" className={classes.articleBody}>{selected.content}</Typography>
								{parseAttachments(selected).length > 0 && (
									<div className={classes.attachSection}>
										<div className={classes.thumbRow}>
											{parseAttachments(selected).filter(a => a.type === 'image').map(renderAttachment)}
										</div>
										{parseAttachments(selected).filter(a => a.type === 'video').map(renderAttachment)}
										{parseAttachments(selected).filter(a => a.type === 'youtube').map(renderAttachment)}
										{parseAttachments(selected).filter(a => a.type === 'file').map(renderAttachment)}
									</div>
								)}
							</div>
						)}
					</Paper>
				</Grid>
			</Grid>
			<ImageViewer
				open={Boolean(viewerImage)}
				onDismiss={() => setViewerImage(null)}
				src={viewerImage}
			/>
		</div>
	);
}
